/**
 * Fenestration IDF Synchronization
 *
 * Creates and updates FenestrationSurface:Detailed IDF objects
 * from geometry editing operations.
 */

import type { IDFObject, IDFField } from '../../parser/types';
import type { Vector3, FenestrationType } from '../types';
import type { FenestrationPlaceConfig } from '../editingTypes';
import { FENESTRATION_FIELD_MAPPINGS, fenestrationTypeToIDF } from './fieldMappings';

/**
 * Result of creating a fenestration IDF object.
 */
export interface FenestrationIDFResult {
  /** The created IDF object */
  fenestration: Omit<IDFObject, 'id'>;
}

/**
 * Create a field object for IDF.
 */
function createField(value: string, comment?: string): IDFField {
  return {
    value,
    ...(comment && { inlineComment: `!- ${comment}` }),
  };
}

/**
 * Calculate fenestration vertices on a parent surface.
 * Places the fenestration at the specified horizontal position and sill height.
 */
export function calculateFenestrationVertices(
  parentVertices: Vector3[],
  width: number,
  height: number,
  sillHeight: number,
  horizontalPosition: number
): Vector3[] {
  if (parentVertices.length < 3) {
    return [];
  }

  // Ensure we have exactly 4 vertices for the parent (rectangular wall)
  // For non-rectangular walls, we use the first two bottom vertices
  const v0 = parentVertices[0];
  const v1 = parentVertices[1];
  const v2 = parentVertices[2];
  const v3 = parentVertices[3] ?? parentVertices[2];
  if (!v0 || !v1 || !v2 || !v3) return [];

  // Calculate the wall vectors
  // bottomLeft to bottomRight (horizontal direction)
  const horizontalVec = {
    x: v1.x - v0.x,
    y: v1.y - v0.y,
    z: v1.z - v0.z,
  };

  // bottomLeft to topLeft (vertical direction)
  const verticalVec = {
    x: v3.x - v0.x,
    y: v3.y - v0.y,
    z: v3.z - v0.z,
  };

  // Calculate wall dimensions
  const wallWidth = Math.sqrt(
    horizontalVec.x ** 2 + horizontalVec.y ** 2 + horizontalVec.z ** 2
  );
  const wallHeight = Math.sqrt(
    verticalVec.x ** 2 + verticalVec.y ** 2 + verticalVec.z ** 2
  );

  // Normalize vectors
  const hNorm = {
    x: horizontalVec.x / wallWidth,
    y: horizontalVec.y / wallWidth,
    z: horizontalVec.z / wallWidth,
  };
  const vNorm = {
    x: verticalVec.x / wallHeight,
    y: verticalVec.y / wallHeight,
    z: verticalVec.z / wallHeight,
  };

  // Calculate fenestration position
  // horizontalPosition is 0-1, where 0.5 is center
  const availableWidth = wallWidth - width;
  const hOffset = availableWidth * horizontalPosition + width / 2;

  // Fenestration corners (counter-clockwise from bottom-left of fenestration)
  const bottomLeft = {
    x: v0.x + hNorm.x * (hOffset - width / 2) + vNorm.x * sillHeight,
    y: v0.y + hNorm.y * (hOffset - width / 2) + vNorm.y * sillHeight,
    z: v0.z + hNorm.z * (hOffset - width / 2) + vNorm.z * sillHeight,
  };

  const bottomRight = {
    x: bottomLeft.x + hNorm.x * width,
    y: bottomLeft.y + hNorm.y * width,
    z: bottomLeft.z + hNorm.z * width,
  };

  const topRight = {
    x: bottomRight.x + vNorm.x * height,
    y: bottomRight.y + vNorm.y * height,
    z: bottomRight.z + vNorm.z * height,
  };

  const topLeft = {
    x: bottomLeft.x + vNorm.x * height,
    y: bottomLeft.y + vNorm.y * height,
    z: bottomLeft.z + vNorm.z * height,
  };

  return [bottomLeft, bottomRight, topRight, topLeft];
}

/**
 * Create a new FenestrationSurface:Detailed IDF object.
 */
export function createFenestrationIDFObject(
  config: FenestrationPlaceConfig,
  parentSurfaceName: string,
  parentVertices: Vector3[]
): FenestrationIDFResult {
  const {
    name,
    type,
    construction,
    width,
    height,
    sillHeight,
    horizontalPosition,
    frameAndDivider,
    multiplier = 1,
  } = config;

  // Calculate fenestration vertices
  const vertices = calculateFenestrationVertices(
    parentVertices,
    width,
    height,
    sillHeight,
    horizontalPosition
  );

  const fields: IDFField[] = [
    createField(name, 'Name'),
    createField(fenestrationTypeToIDF(type), 'Surface Type'),
    createField(construction, 'Construction Name'),
    createField(parentSurfaceName, 'Building Surface Name'),
    createField('', 'Outside Boundary Condition Object'),
    createField('autocalculate', 'View Factor to Ground'),
    createField(frameAndDivider || '', 'Frame and Divider Name'),
    createField(String(multiplier), 'Multiplier'),
    createField(String(vertices.length), 'Number of Vertices'),
  ];

  // Add vertices
  vertices.forEach((v, i) => {
    fields.push(createField(v.x.toFixed(6), `Vertex ${i + 1} X-coordinate`));
    fields.push(createField(v.y.toFixed(6), `Vertex ${i + 1} Y-coordinate`));
    fields.push(createField(v.z.toFixed(6), `Vertex ${i + 1} Z-coordinate`));
  });

  return {
    fenestration: {
      className: 'FenestrationSurface:Detailed',
      fields,
      leadingComments: [],
    },
  };
}

/**
 * Update a fenestration IDF object.
 */
export function updateFenestrationIDFObject(
  fenestrationObject: IDFObject,
  updates: {
    name?: string;
    type?: FenestrationType;
    construction?: string;
    parentSurface?: string;
    frameAndDivider?: string;
    multiplier?: number;
    vertices?: Vector3[];
  }
): IDFObject {
  const fields = [...fenestrationObject.fields];

  if (updates.name !== undefined) {
    if (fields[FENESTRATION_FIELD_MAPPINGS.NAME.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.NAME.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.NAME.index],
        value: updates.name,
      };
    }
  }

  if (updates.type !== undefined) {
    if (fields[FENESTRATION_FIELD_MAPPINGS.SURFACE_TYPE.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.SURFACE_TYPE.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.SURFACE_TYPE.index],
        value: fenestrationTypeToIDF(updates.type),
      };
    }
  }

  if (updates.construction !== undefined) {
    if (fields[FENESTRATION_FIELD_MAPPINGS.CONSTRUCTION_NAME.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.CONSTRUCTION_NAME.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.CONSTRUCTION_NAME.index],
        value: updates.construction,
      };
    }
  }

  if (updates.parentSurface !== undefined) {
    if (fields[FENESTRATION_FIELD_MAPPINGS.BUILDING_SURFACE_NAME.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.BUILDING_SURFACE_NAME.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.BUILDING_SURFACE_NAME.index],
        value: updates.parentSurface,
      };
    }
  }

  if (updates.frameAndDivider !== undefined) {
    if (fields[FENESTRATION_FIELD_MAPPINGS.FRAME_AND_DIVIDER_NAME.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.FRAME_AND_DIVIDER_NAME.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.FRAME_AND_DIVIDER_NAME.index],
        value: updates.frameAndDivider,
      };
    }
  }

  if (updates.multiplier !== undefined) {
    if (fields[FENESTRATION_FIELD_MAPPINGS.MULTIPLIER.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.MULTIPLIER.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.MULTIPLIER.index],
        value: String(updates.multiplier),
      };
    }
  }

  if (updates.vertices !== undefined) {
    // Update number of vertices
    if (fields[FENESTRATION_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index]) {
      fields[FENESTRATION_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index] = {
        ...fields[FENESTRATION_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index],
        value: String(updates.vertices.length),
      };
    }

    // Remove old vertices
    fields.splice(FENESTRATION_FIELD_MAPPINGS.VERTICES_START.index);

    // Add new vertices
    updates.vertices.forEach((v, i) => {
      fields.push(createField(v.x.toFixed(6), `Vertex ${i + 1} X-coordinate`));
      fields.push(createField(v.y.toFixed(6), `Vertex ${i + 1} Y-coordinate`));
      fields.push(createField(v.z.toFixed(6), `Vertex ${i + 1} Z-coordinate`));
    });
  }

  return { ...fenestrationObject, fields };
}

/**
 * Get fenestration vertices from IDF object.
 */
export function getFenestrationVertices(fenestrationObject: IDFObject): Vector3[] {
  const vertices: Vector3[] = [];
  const numVerticesField = fenestrationObject.fields[FENESTRATION_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index];
  const numVertices = numVerticesField ? parseInt(numVerticesField.value) : 0;

  for (let i = 0; i < numVertices; i++) {
    const baseIndex = FENESTRATION_FIELD_MAPPINGS.VERTICES_START.index + i * 3;
    const xField = fenestrationObject.fields[baseIndex];
    const yField = fenestrationObject.fields[baseIndex + 1];
    const zField = fenestrationObject.fields[baseIndex + 2];

    if (xField && yField && zField) {
      vertices.push({
        x: parseFloat(xField.value) || 0,
        y: parseFloat(yField.value) || 0,
        z: parseFloat(zField.value) || 0,
      });
    }
  }

  return vertices;
}

/**
 * Get fenestration name from IDF object.
 */
export function getFenestrationName(fenestrationObject: IDFObject): string {
  return fenestrationObject.fields[FENESTRATION_FIELD_MAPPINGS.NAME.index]?.value ?? '';
}

/**
 * Get fenestration parent surface name from IDF object.
 */
export function getFenestrationParentSurface(fenestrationObject: IDFObject): string {
  return fenestrationObject.fields[FENESTRATION_FIELD_MAPPINGS.BUILDING_SURFACE_NAME.index]?.value ?? '';
}

/**
 * Get fenestration construction from IDF object.
 */
export function getFenestrationConstruction(fenestrationObject: IDFObject): string {
  return fenestrationObject.fields[FENESTRATION_FIELD_MAPPINGS.CONSTRUCTION_NAME.index]?.value ?? '';
}

/**
 * Move fenestration vertices by a translation vector.
 */
export function moveFenestrationVertices(
  fenestrationObject: IDFObject,
  translation: Vector3
): IDFObject {
  const vertices = getFenestrationVertices(fenestrationObject);
  const movedVertices = vertices.map((v) => ({
    x: v.x + translation.x,
    y: v.y + translation.y,
    z: v.z + translation.z,
  }));

  return updateFenestrationIDFObject(fenestrationObject, { vertices: movedVertices });
}

/**
 * Calculate fenestration area.
 */
export function calculateFenestrationArea(vertices: Vector3[]): number {
  if (vertices.length < 3) return 0;

  let area = 0;
  const n = vertices.length;

  for (let i = 1; i < n - 1; i++) {
    const v0 = vertices[0];
    const v1 = vertices[i];
    const v2 = vertices[i + 1];
    if (!v0 || !v1 || !v2) continue;

    // Triangle area using cross product
    const ax = v1.x - v0.x;
    const ay = v1.y - v0.y;
    const az = v1.z - v0.z;
    const bx = v2.x - v0.x;
    const by = v2.y - v0.y;
    const bz = v2.z - v0.z;

    const cx = ay * bz - az * by;
    const cy = az * bx - ax * bz;
    const cz = ax * by - ay * bx;

    area += Math.sqrt(cx * cx + cy * cy + cz * cz) / 2;
  }

  return area;
}
